import React from 'react';
import { Trash2 } from 'lucide-react';
import HistoryFilter from './HistoryFilter';
import Pagination from '../common/Pagination';
import HistoryListSkeleton from './components/HistoryListSkeleton';
import { renderStatusBadge } from '../../utils/statusBadge';
import HighlightedText from '../common/HighlightedText';

// InquiryCard 와 동일한 날짜 포맷 로직
const formatDateTime = (raw) => {
    if (!raw) return '';
    const d = new Date(String(raw).replace(' ', 'T'));
    if (isNaN(d.getTime())) return raw;
    const date = `${d.getMonth() + 1}/${d.getDate()}`;
    const time = d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false });
    return `${date} ${time}`;
};

// InquiryCard 와 동일한 삭제 권한 로직
const canDeleteItem = (item, currentUser) => {
    if (!currentUser) return false;
    if (currentUser.role === 'R0') return true;
    if (currentUser.role === 'R2') return item.company_code === currentUser.company;
    return String(item.user_id) === String(currentUser.id);
};

/**
 * 질의 현황 탭의 최상위 조립 컴포넌트.
 * HistoryFilter, 정형 테이블(반응형), Pagination을 조합합니다.
 */
const HistoryView = ({
    viewMode = 'personal',
    currentUser,
    paginatedData = [],
    totalPages = 1,
    currentPage = 1,
    setCurrentPage,
    onSelectInquiry,
    onDeleteInquiry,
    personalSearch = '',
    setPersonalSearch,
    companyNameSearch = '',
    setCompanyNameSearch,
    companyContentSearch = '',
    setCompanyContentSearch,
    adminCompanySearch = '',
    setAdminCompanySearch,
    startDate = '',
    setStartDate,
    endDate = '',
    setEndDate,
    clearDateFilter,
    statusFilter = 'all',
    setStatusFilter,
    sortOrder = 'desc',
    setSortOrder,
    isLoadingHistory = false,
}) => {
    const isAdmin        = viewMode === 'admin';
    const isCompany      = viewMode === 'company';
    const showNameCol    = isAdmin || isCompany;
    const showCompanyCol = isAdmin;

    // viewMode 별 질문 하이라이트
    const questionHighlight = viewMode === 'personal' ? personalSearch : companyContentSearch;

    const rows = paginatedData ?? [];

    return (
        <div className="space-y-6 pt-4">
            {/* 검색 필터 — 기존과 동일 */}
            <HistoryFilter
                viewMode={viewMode}
                personalSearch={personalSearch ?? ''}
                setPersonalSearch={setPersonalSearch}
                companyNameSearch={companyNameSearch ?? ''}
                setCompanyNameSearch={setCompanyNameSearch}
                companyContentSearch={companyContentSearch ?? ''}
                setCompanyContentSearch={setCompanyContentSearch}
                adminCompanySearch={adminCompanySearch ?? ''}
                setAdminCompanySearch={setAdminCompanySearch}
                startDate={startDate ?? ''}
                setStartDate={setStartDate}
                endDate={endDate ?? ''}
                setEndDate={setEndDate}
                clearDateFilter={clearDateFilter}
                statusFilter={statusFilter ?? 'all'}
                setStatusFilter={setStatusFilter}
                sortOrder={sortOrder ?? 'desc'}
                setSortOrder={setSortOrder}
            />

            {/* 테이블 영역 */}
            <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
                {isLoadingHistory ? (
                    <div className="p-4">
                        <HistoryListSkeleton count={5} />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse text-left">
                            <thead>
                                <tr className="border-b-2 border-gray-100 bg-gray-50/60">
                                    {/* 일시: 항상 표시 */}
                                    <th className="whitespace-nowrap px-3 py-2.5 sm:px-4 sm:py-3 text-sm font-bold uppercase tracking-wide text-gray-400">
                                        일시
                                    </th>
                                    {/* 이름: sm 이상에서만 표시 */}
                                    {showNameCol && (
                                        <th className="hidden sm:table-cell whitespace-nowrap px-3 py-2.5 sm:px-4 sm:py-3 text-sm font-bold uppercase tracking-wide text-gray-400">
                                            이름
                                        </th>
                                    )}
                                    {/* 회사/부서: md 이상에서만 표시 */}
                                    {showCompanyCol && (
                                        <th className="hidden md:table-cell whitespace-nowrap px-3 py-2.5 sm:px-4 sm:py-3 text-sm font-bold uppercase tracking-wide text-gray-400">
                                            회사/부서
                                        </th>
                                    )}
                                    {/* 질문: 항상 표시 */}
                                    <th className="px-3 py-2.5 sm:px-4 sm:py-3 text-sm font-bold uppercase tracking-wide text-gray-400">
                                        질문
                                    </th>
                                    {/* 상태: 항상 표시 */}
                                    <th className="whitespace-nowrap px-3 py-2.5 sm:px-4 sm:py-3 text-sm font-bold uppercase tracking-wide text-gray-400">
                                        상태
                                    </th>
                                    {/* 액션: 항상 표시 */}
                                    <th className="px-3 py-2.5 sm:px-4 sm:py-3" />
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan={4 + (showNameCol ? 1 : 0) + (showCompanyCol ? 1 : 0)}
                                            className="py-16 text-center text-base text-gray-400"
                                        >
                                            해당 조건에 맞는 질의 내역이 없습니다.
                                        </td>
                                    </tr>
                                ) : (
                                    rows.map((item) => {
                                        const showDelete = canDeleteItem(item, currentUser);
                                        const isError    = item.answer_status === 'error' || item.answer_status === 'timeout';

                                        return (
                                            <tr
                                                key={item.id}
                                                className="cursor-pointer border-b border-gray-50 transition hover:bg-blue-50/30"
                                                onClick={() => onSelectInquiry?.(item)}
                                            >
                                                {/* 일시 */}
                                                <td className="whitespace-nowrap px-3 py-2.5 sm:px-4 sm:py-3 font-mono text-xs sm:text-sm text-gray-500">
                                                    {formatDateTime(item.created_at)}
                                                </td>

                                                {/* 이름: sm 이상 */}
                                                {showNameCol && (
                                                    <td className="hidden sm:table-cell whitespace-nowrap px-3 py-2.5 sm:px-4 sm:py-3 text-sm font-semibold text-gray-800">
                                                        <HighlightedText
                                                            text={item.user_name}
                                                            highlight={isCompany ? companyNameSearch : ''}
                                                        />
                                                    </td>
                                                )}

                                                {/* 회사/부서: md 이상 */}
                                                {showCompanyCol && (
                                                    <td className="hidden md:table-cell px-3 py-2.5 sm:px-4 sm:py-3">
                                                        <p className="text-sm font-semibold text-gray-900">
                                                            <HighlightedText
                                                                text={item.company_name || item.company_code}
                                                                highlight={adminCompanySearch}
                                                            />
                                                        </p>
                                                        {item.company_name && (
                                                            <p className="text-xs text-gray-400">{item.company_code}</p>
                                                        )}
                                                    </td>
                                                )}

                                                {/* 질문 */}
                                                <td className="max-w-[140px] sm:max-w-[260px] px-3 py-2.5 sm:px-4 sm:py-3">
                                                    <p className={`truncate text-sm font-medium ${isError ? 'text-red-700' : 'text-gray-700'}`}>
                                                        <HighlightedText text={item.question} highlight={questionHighlight} />
                                                    </p>
                                                </td>

                                                {/* 상태 */}
                                                <td className="px-3 py-2.5 sm:px-4 sm:py-3">
                                                    {renderStatusBadge(item.answer_status)}
                                                </td>

                                                {/* 액션 */}
                                                <td className="px-3 py-2.5 sm:px-4 sm:py-3 text-right" onClick={(e) => e.stopPropagation()}>
                                                    {showDelete && (
                                                        <button
                                                            type="button"
                                                            title="삭제"
                                                            className="rounded-md p-1 sm:p-1.5 text-red-400 transition hover:bg-red-50 hover:text-red-600"
                                                            onClick={(e) => { e.stopPropagation(); onDeleteInquiry?.(item.id); }}
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* 페이지네이션 */}
                {!isLoadingHistory && rows.length > 0 && (
                    <div className="border-t border-gray-100">
                        <Pagination
                            currentPage={currentPage ?? 1}
                            totalPages={totalPages ?? 1}
                            onPageChange={(p) => setCurrentPage?.(p)}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default HistoryView;
